import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { Button } from '../Button';
import { ChevronLeftIcon, ChevronRightIcon, CloseIcon, DownloadIcon, ZoomInIcon, ZoomOutIcon } from '../icons';
import { FichiersContext } from './FichiersContext.js';
import { lienTelechargement } from './fichiers.js';
import { nomDeTelechargement } from './photosSpec.js';
import './Fichiers.css';

const ZOOMS = [1, 1.5, 2, 3, 4];

/**
 * Visionneuse des photos (contrat v1.13) : fenêtre modale, piège de focus, Échap ferme, flèches
 * gauche et droite pour la photo précédente ou suivante, « + », « − » et « 0 » pour le zoom
 * (double-clic aussi), téléchargement par le lien du même domaine. Photo cassée : « Photo
 * indisponible ». Retour du focus à l'élément d'origine à la fermeture.
 * items : [{lien, libelle, alt, vehicule?}] ; index : photo ouverte.
 */
export function Lightbox({ items, index: initial = 0, onClose }) {
  const [index, setIndex] = useState(Math.min(Math.max(0, initial), items.length - 1));
  const [zoom, setZoom] = useState(0);
  const [casse, setCasse] = useState(false);
  const rootRef = useRef(null);
  const closeRef = useRef(null);
  const titleId = useId();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const item = items[index];
  const several = items.length > 1;

  useFocusTrap(rootRef, true);

  useEffect(() => {
    setZoom(0);
    setCasse(false);
  }, [index]);

  const previous = useCallback(() => setIndex((i) => (i - 1 + items.length) % items.length), [items.length]);
  const next = useCallback(() => setIndex((i) => (i + 1) % items.length), [items.length]);

  // Focus sur « Fermer » à l'ouverture, rendu à l'élément d'origine à la fermeture
  useEffect(() => {
    const back = document.activeElement;
    const frame = requestAnimationFrame(() => closeRef.current?.focus());
    return () => {
      cancelAnimationFrame(frame);
      requestAnimationFrame(() => {
        if (back?.isConnected) back.focus({ preventScroll: true });
      });
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onCloseRef.current?.();
      } else if (event.key === 'ArrowLeft' && several) {
        event.preventDefault();
        previous();
      } else if (event.key === 'ArrowRight' && several) {
        event.preventDefault();
        next();
      } else if (event.key === '+' || event.key === '=') {
        event.preventDefault();
        setZoom((z) => Math.min(ZOOMS.length - 1, z + 1));
      } else if (event.key === '-' || event.key === '_') {
        event.preventDefault();
        setZoom((z) => Math.max(0, z - 1));
      } else if (event.key === '0') {
        event.preventDefault();
        setZoom(0);
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [several, previous, next]);

  if (!item) return null;
  const factor = ZOOMS[zoom];
  const position = several ? ` (${index + 1} sur ${items.length})` : '';

  return createPortal(
    <div className="lightbox-layer">
      <div className="lightbox-overlay" aria-hidden="true" onClick={() => onCloseRef.current?.()} />
      <div ref={rootRef} className="lightbox" role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}>
        <div className="lightbox__bar">
          <p id={titleId} className="lightbox__title">
            {item.libelle}
            <span className="lightbox__count">{position}</span>
          </p>
          <div className="lightbox__actions">
            <Button variant="ghost" size="sm" icon={ZoomOutIcon} iconOnly label="Zoom arrière (−)" disabled={zoom === 0 || casse} onClick={() => setZoom((z) => Math.max(0, z - 1))} />
            <span className="lightbox__zoom ds-num" aria-live="polite">{Math.round(factor * 100)} %</span>
            <Button variant="ghost" size="sm" icon={ZoomInIcon} iconOnly label="Zoom avant (+)" disabled={zoom === ZOOMS.length - 1 || casse} onClick={() => setZoom((z) => Math.min(ZOOMS.length - 1, z + 1))} />
            <Button as="a" variant="ghost" size="sm" icon={DownloadIcon} iconOnly label="Télécharger la photo" href={lienTelechargement(item.lien, nomDeTelechargement(item.libelle, item.vehicule))} download="" />
            <Button ref={closeRef} variant="ghost" size="sm" icon={CloseIcon} iconOnly label="Fermer (Échap)" onClick={() => onCloseRef.current?.()} />
          </div>
        </div>
        <div className={`lightbox__stage ${factor > 1 ? 'is-zoomed' : ''}`.trim()}>
          {casse ? (
            <p className="lightbox__broken" role="status">Photo indisponible</p>
          ) : (
            <img
              key={item.lien}
              className="lightbox__image"
              src={item.lien}
              alt={item.alt || item.libelle}
              decoding="async"
              style={factor > 1 ? { width: `${factor * 100}%` } : undefined}
              onError={() => setCasse(true)}
              onDoubleClick={() => setZoom((z) => (z === 0 ? 2 : 0))}
            />
          )}
        </div>
        {several && (
          <>
            <Button variant="outline" icon={ChevronLeftIcon} iconOnly label="Photo précédente (←)" className="lightbox__nav lightbox__nav--prev" onClick={previous} />
            <Button variant="outline" icon={ChevronRightIcon} iconOnly label="Photo suivante (→)" className="lightbox__nav lightbox__nav--next" onClick={next} />
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

/**
 * Fournit à la conversation ses liens vérifiés (`liens`, Map de fichiers.js) et la visionneuse :
 * ouvrir(items, index) l'ouvre, une seule à la fois.
 */
export function FichiersProvider({ liens, children }) {
  const [ouverte, setOuverte] = useState(null);
  const ouvrir = useCallback((items, index = 0) => {
    const list = (Array.isArray(items) ? items : []).filter((it) => it && typeof it.lien === 'string');
    if (list.length) setOuverte({ items: list, index });
  }, []);
  const value = useMemo(() => ({ liens, ouvrir }), [liens, ouvrir]);
  return (
    <FichiersContext.Provider value={value}>
      {children}
      {ouverte && <Lightbox items={ouverte.items} index={ouverte.index} onClose={() => setOuverte(null)} />}
    </FichiersContext.Provider>
  );
}
